import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface PortfolioPreviewModalProps {
  /** 是否显示弹窗 */
  open: boolean;
  /** 作品名称 */
  title?: string;
  /** 作品简介描述 */
  intro?: string;
  /** 是否为视频类型 (PORTFOLIO_VIDEO) */
  isVideo?: boolean;
  /** 媒体访问链接 (图片链接或视频 access_url) */
  mediaUrl?: string | null;
  /** 关闭回调 */
  onClose: () => void;
  /** 堆叠层级 (默认 10000) */
  zIndex?: number;
}

export default function PortfolioPreviewModal({
  open,
  title = '作品预览',
  intro,
  isVideo = false,
  mediaUrl,
  onClose,
  zIndex = 10000,
}: PortfolioPreviewModalProps) {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  // 动画过渡处理
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
        });
      });
    } else {
      setAnimating(false);
      timer = setTimeout(() => {
        setVisible(false);
      }, 250);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open]);

  // ESC 键盘监听
  useEffect(() => {
    if (!open || !visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, visible, onClose]);

  if (!visible) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div
      style={{ zIndex }}
      onClick={handleOverlayClick}
      className={`fixed inset-0 flex flex-col items-center justify-center p-4 md:p-8 transition-all duration-300 ease-out select-none ${
        animating
          ? 'bg-black/80 backdrop-blur-xl opacity-100'
          : 'bg-black/0 backdrop-blur-none opacity-0 pointer-events-none'
      }`}
    >
      {/* 悬浮关闭按钮 */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 text-white backdrop-blur-md flex items-center justify-center transition-all cursor-pointer shadow-lg hover:scale-105 active:scale-95"
          title="关闭 (Esc)"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      {/* 核心内容区域：拉满 80% 屏幕高度 */}
      <div
        className={`relative flex flex-col items-center justify-center max-w-[95vw] h-[80vh] transition-all duration-300 ease-out transform ${
          animating ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'
        }`}
      >
        {/* 标题信息 (仅当提供有效 title 且非默认值时展示) */}
        {title && title !== '作品预览' && (
          <div className="mb-2 text-center max-w-2xl px-4 pointer-events-none shrink-0">
            <h3 className="font-medium text-sm md:text-base text-white/90 drop-shadow-md truncate">
              {title}
            </h3>
          </div>
        )}

        {/* 媒体展示：高度尽量拉满 80vh 屏幕高度，按真实比例居中适应 */}
        <div className="relative flex-1 w-full h-[80vh] flex items-center justify-center overflow-hidden rounded-2xl">
          {mediaUrl ? (
            isVideo ? (
              <video
                src={mediaUrl}
                controls
                autoPlay
                className="h-[80vh] max-h-[80vh] w-auto max-w-[95vw] rounded-2xl object-contain shadow-2xl bg-black/40"
              />
            ) : (
              <img
                src={mediaUrl}
                alt={title || '预览图片'}
                className="h-[80vh] max-h-[80vh] w-auto max-w-[95vw] rounded-2xl object-contain shadow-2xl"
              />
            )
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-8 text-white/60">
              <i className={`${isVideo ? 'ri-vidicon-line' : 'ri-image-line'} text-5xl mb-3 text-white/40`}></i>
              <p className="text-sm font-light">未找到媒体资源文件</p>
            </div>
          )}
        </div>

        {/* 描述信息 */}
        {intro && (
          <div className="mt-2 text-center max-w-2xl px-4 pointer-events-none shrink-0">
            <p className="text-xs md:text-sm text-white/70 leading-relaxed drop-shadow-sm whitespace-pre-line">
              {intro}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
